import * as React from 'react'
import classNames from 'classnames'
import { renderCatch } from '../error/renderCatch'
import type { CommonDeps } from '../base/CommonDeps'
import type { Profile } from '../user/Profile'
import type { RichTextComment } from '../../../shared/domain/postComments/types.no-deps'
import type { RichTextParagraph } from '../../../shared/domain/richText/types.no-deps'
import { formatTimelineDate } from './formatTimelineDate'
import { SVG_Ellipsis } from '../svg/SVG_Ellipsis'
import type { UUID } from '../../../shared/lib/typeHelpers.no-deps'
import { SVG_Delete } from '../svg/SVG_Delete'
import { TimelineItem, type TimelineItemPosition } from '../ui/TimelineItem'
import i18next from 'i18next'
import { CommentEditor } from './CommentEditor'
import type { LexicalEditor } from 'lexical'
import { toSlate, fromSlate } from '../lexical/helpers/convert'
import { getJson } from '../lexical/helpers/editor'
import { isEmpty } from '../../../shared/domain/richText/helpers.no-deps'
import type { Nullable } from '@zaptic-external/zorm/dist/helpers/types'
import { TimelineUser } from '../../../shared/webcomponents/react-wrappers/TimelineUser'
import { BlockQuote } from '../ui/BlockQuote'

export const timelineCommentDomId = (commentId: UUID) => `timeline-comment-${commentId}`

interface Props {
    deps: CommonDeps
    quote?: string
    comment: RichTextComment
    position: TimelineItemPosition
    profile: Profile
    onActionClick: (id: UUID) => void
    onClick: (comment: RichTextComment) => void
    onQuoteClick?: () => void
    routeToUserPage?: (userId: number) => void
    editingCommentTextData?: {
        commentId: UUID
        onTextSave: (newValue: RichTextParagraph[]) => void
        onCancel: () => void
    }

    // this will be true for portal context menu
    showContextMenu: boolean

    showVerticalLine?: boolean

    // this will be received when user clicks on any comment actions
    contextMenuData?: {
        commentId: UUID
        displayMenu: boolean
        onEdit: (commentId: string) => void
        onDelete: (commentId: string) => void
    }

    allowTranslation: boolean

    currentUserId: number
    profiles: Profile[]
    getProfile: (userId: number) => Profile
    fetchTranslation?: (comment: RichTextComment) => Promise<RichTextComment>
    mentionsBelow?: boolean
}

interface State {
    translation: Nullable<RichTextComment>
    isTranslating: boolean
    isTranslationVisible: boolean
}

export const timelineCommentActionsDomId = (commmentId: UUID) => `TimelineComment-actions-${commmentId}`

export class TimelineComment extends React.Component<Props, State> {
    private editorRef: React.RefObject<LexicalEditor>

    constructor(props: Props) {
        super(props)

        this.state = {
            translation: null,
            isTranslating: false,
            isTranslationVisible: false,
        }

        this.editorRef = React.createRef()
    }

    public render = () => renderCatch(this.props.deps, this.tryRender)

    private handleCommentClick = () => {
        const { comment, editingCommentTextData, onClick } = this.props
        // Ignore clicks on comments when editing
        if (!editingCommentTextData && onClick) onClick(comment)
    }

    private handleActionClick = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation()
        const { comment, editingCommentTextData, onActionClick } = this.props
        // Ignore clicks on the action button when editing
        if (!editingCommentTextData) onActionClick(comment.id)
    }

    private handleContextMenuEdit = (event: React.MouseEvent<HTMLLIElement>) => {
        event.stopPropagation()
        const { comment, contextMenuData } = this.props

        contextMenuData.onEdit(comment.id)
    }

    private handleContextMenuDelete = (event: React.MouseEvent<HTMLLIElement>) => {
        event.stopPropagation()
        const { comment, contextMenuData } = this.props
        contextMenuData.onDelete(comment.id)
    }

    private handleTranslationButtonClick = async (event: React.MouseEvent) => {
        event.stopPropagation()

        if (this.state.isTranslationVisible) {
            return this.setState({ isTranslationVisible: false })
        }

        if (this.state.translation) {
            return this.setState({ isTranslationVisible: true }) // Cached translation
        }

        this.setState({ isTranslating: true })
        try {
            const comment = await this.props.fetchTranslation(this.props.comment)
            this.setState({ translation: comment, isTranslationVisible: true })
        } finally {
            this.setState({ isTranslating: false })
        }
    }

    private handleUserClick = (userId: number) => {
        if (!this.props.routeToUserPage) {
            return
        }

        const userProfile = this.props.getProfile(userId)
        if (!userProfile) {
            return
        }

        this.props.routeToUserPage(userId)
    }

    private renderCommentText = () => {
        const { comment, editingCommentTextData, profiles } = this.props
        const { translation, isTranslationVisible } = this.state

        if (editingCommentTextData && editingCommentTextData.commentId === comment.id) {
            return (
                <div className="AppCore__commentTimeline__TimelineComment__editingComment">
                    <CommentEditor
                        initialContent={fromSlate(comment.text)}
                        profiles={profiles}
                        disabled={false}
                        ref={this.editorRef}
                        language={this.props.deps.language}
                        onUserClick={this.handleUserClick}
                        mentionsBelow={this.props.mentionsBelow}
                    />
                    <div className="AppCore__commentTimeline__TimelineComment__editingCommentActions">
                        <div
                            className="AppCore__commentTimeline__TimelineComment__editingCommentSave"
                            onClick={() => {
                                const content = toSlate(getJson(this.editorRef.current))
                                if (isEmpty(content)) {
                                    return
                                }
                                this.setState({ translation: null, isTranslationVisible: false })
                                editingCommentTextData.onTextSave(content)
                            }}
                        >
                            <span>{i18next.t('appcore:common.save')}</span>
                        </div>
                        <div
                            className="AppCore__commentTimeline__TimelineComment__editingCommentCancel"
                            onClick={editingCommentTextData.onCancel}
                        >
                            <span>{i18next.t('appcore:common.cancel')}</span>
                        </div>
                    </div>
                </div>
            )
        }

        if (comment.isDeleted) {
            return (
                <div className="AppCore__commentTimeline__TimelineComment__text">
                    <div className="AppCore__commentTimeline__TimelineComment__deletedIcon">
                        <SVG_Delete color="#7e8b9e" />
                    </div>
                    <div className="AppCore__commentTimeline__TimelineComment__deletedText">
                        {i18next.t('appcore:TimelineComment.deleted')}
                    </div>
                </div>
            )
        }

        // Don't show the translated text when editing the comment.
        const commentText = isTranslationVisible && !editingCommentTextData ? translation.text : comment.text
        const key = `${isTranslationVisible}-${comment.lastModifiedAt}`

        return (
            <CommentEditor
                key={key}
                initialContent={fromSlate(commentText)}
                profiles={profiles}
                disabled={true}
                ref={this.editorRef}
                language={this.props.deps.language}
                onUserClick={this.handleUserClick}
            />
        )
    }

    private renderContextMenu = () => {
        const { comment, contextMenuData } = this.props

        // This will be set to true for portal as we want to see context menu next to comment
        if (
            this.props.showContextMenu &&
            contextMenuData &&
            contextMenuData.commentId === comment.id &&
            contextMenuData.displayMenu
        ) {
            return (
                <ul className="AppCore__commentTimeline__TimelineComment-actions-list">
                    <li
                        className="AppCore__commentTimeline__TimelineComment-actions-list-item"
                        onClick={this.handleContextMenuEdit}
                    >
                        {i18next.t('appcore:common.edit')}
                    </li>
                    <li
                        id="delete-item"
                        className="AppCore__commentTimeline__TimelineComment-actions-list-item"
                        onClick={this.handleContextMenuDelete}
                    >
                        {i18next.t('appcore:common.delete')}
                    </li>
                </ul>
            )
        }

        return null
    }

    private renderActionButton = () => {
        const { comment, currentUserId } = this.props

        if (comment.isDeleted || comment.createdBy !== currentUserId) return null

        return (
            <div
                className="AppCore__commentTimeline__TimelineComment__actions"
                id={timelineCommentActionsDomId(comment.id)}
                onClick={this.handleActionClick}
            >
                <SVG_Ellipsis />
                {this.renderContextMenu()}
            </div>
        )
    }

    private tryRender = () => {
        const {
            allowTranslation,
            comment,
            deps,
            editingCommentTextData,
            fetchTranslation,
            profile,
            position,
            routeToUserPage,
            showVerticalLine = true,
        } = this.props
        const { isTranslationVisible, isTranslating } = this.state
        const canTranslate =
            allowTranslation &&
            !isTranslationVisible &&
            !editingCommentTextData &&
            typeof fetchTranslation === 'function' &&
            comment.language &&
            !comment.isDeleted &&
            comment.language !== deps.language.split('-')[0] // Example of lang code format: es-ES

        const getAuthorName = () => {
            const deletedUserTranslation = i18next.t('appcore:User.unknown')

            return routeToUserPage ? (
                <TimelineUser
                    key={profile.userId}
                    name={profile.isDeleted ? deletedUserTranslation : profile.name}
                    type={profile.isDeleted ? 'plain' : 'link'}
                    onClick={() => (profile.isDeleted ? undefined : routeToUserPage(profile.userId))}
                />
            ) : (
                profile.name
            )
        }

        return (
            <div
                id={timelineCommentDomId(comment.id)}
                className={classNames('AppCore__commentTimeline__TimelineComment', {
                    'AppCore__commentTimeline__TimelineComment-deleted': comment.isDeleted,
                })}
                onClick={this.handleCommentClick}
            >
                <TimelineItem
                    profile={profile}
                    displayWithBox={true}
                    displayVerticalLine={showVerticalLine}
                    position={position}
                    onCircleClick={() => this.handleUserClick(profile.userId)}
                    besideCircle={
                        <div className="AppCore__commentTimeline__TimelineComment__topRow">
                            <div className="AppCore__commentTimeline__TimelineComment__name">{getAuthorName()}</div>
                            <div className="AppCore__commentTimeline__TimelineComment__date">
                                {formatTimelineDate(comment.createdAt)}
                            </div>
                            {this.renderActionButton()}
                        </div>
                    }
                    belowCircle={
                        <>
                            <div className="AppCore__commentTimeline__TimelineComment__commentText">
                                {!comment.isDeleted && this.props.quote && (
                                    <BlockQuote onClick={this.props.onQuoteClick}>{this.props.quote}</BlockQuote>
                                )}
                                {this.renderCommentText()}
                            </div>
                            <div className="AppCore__commentTimeline__TimelineComment__bottomRow">
                                {(canTranslate || isTranslationVisible) && !editingCommentTextData && (
                                    <zaptic-button
                                        type="link"
                                        size="small"
                                        tall
                                        loading={isTranslating}
                                        data-testid={`comment-${comment.id}-${
                                            canTranslate ? 'see-translation-btn' : 'see-original-btn'
                                        }`}
                                        onClick={this.handleTranslationButtonClick}
                                    >
                                        {isTranslationVisible
                                            ? i18next.t('appcore:common.seeOriginal')
                                            : i18next.t('appcore:common.seeTranslation')}
                                    </zaptic-button>
                                )}
                            </div>
                        </>
                    }
                />
            </div>
        )
    }
}
